import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { MAX_UPLOAD_BYTES } from '../../lib/api';
import { Avatar, PageHead, Section, Spinner } from '../../components/ui';
import RichTextEditor from '../../components/RichTextEditor';
import ColorField, { cssBackground } from '../../components/ColorField';
import IconColorField from '../../components/IconColorField';
import Modal from '../../components/Modal';
import { LessonPreviewBody } from '../LessonPlayerPage';
import '../../styles/learn.css';

const ADD = [
  { kind: 'RICH_TEXT', label: '¶ Text' },
  { kind: 'REFLECTION', label: '📝 Reflection' },
  { kind: 'AUDIO', label: '♪ Audio' },
  { kind: 'VIDEO', label: '▶ Video' },
  { kind: 'DOC', label: '📄 Doc' },
  { kind: 'TYPEFORM', label: '🧾 Embed Form', title: 'Embed external form' },
  { kind: 'YOUTUBE', label: '▶ Embed YouTube' },
];
const isMedia = (k) => k === 'AUDIO' || k === 'VIDEO' || k === 'DOC';
const isEmbed = (k) => k === 'TYPEFORM' || k === 'YOUTUBE';
const TIERS = ['FREE', 'PRO'];

export default function LessonEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);   // { title, subtitle, tier, status, durationSeconds, courseId }
  const [blocks, setBlocks] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.adminGetLesson(id), api.adminGetBlocks(id)])
      .then(([l, bs]) => {
        if (cancelled) return;
        setMeta({
          title: l.title || '', subtitle: l.subtitle || '', tier: l.tier || 'FREE',
          status: l.status || 'DRAFT', durationSeconds: l.durationSeconds ?? null, courseId: l.courseId,
          requiresPrevious: !!l.requiresPrevious, headerColor: l.headerColor || '',
          askAi: l.askAi !== false, hasCover: !!l.coverImageUri, iconColor: l.iconColor || '',
        });
        setBlocks((Array.isArray(bs) ? bs : []).map((b) => ({
          kind: b.kind, text: b.text || '', mediaUri: b.mediaUri || null,
          mediaContentType: b.mediaContentType || null, mediaFilename: b.mediaFilename || null,
        })));
      })
      .catch((e) => { if (!cancelled) { alert(e.message); setBlocks([]); setMeta({}); } });
    return () => { cancelled = true; };
  }, [id]);

  const setM = (k) => (e) => setMeta((m) => ({ ...m, [k]: e.target.value }));
  const update = (i, patch) => setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const remove = (i) => setBlocks((bs) => bs.filter((_, j) => j !== i));
  const move = (i, dir) => setBlocks((bs) => {
    const j = i + dir;
    if (j < 0 || j >= bs.length) return bs;
    const copy = [...bs];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const add = (kind) => setBlocks((bs) => [...bs, { kind, text: '', mediaUri: null, mediaContentType: null, mediaFilename: null }]);

  // Adding a media block opens the file picker first, uploads, then inserts the
  // block already populated — no empty block with a hunt-for-the-upload-button.
  const newFileRef = useRef(null);
  const [addKind, setAddKind] = useState(null);   // which media kind is being added
  const [addingMedia, setAddingMedia] = useState(false);
  const ACCEPT = { AUDIO: 'audio/*', VIDEO: 'video/*', DOC: '.pdf,.docx,.txt,.md,.csv,image/*' };

  const startAdd = (kind) => {
    if (!isMedia(kind)) { add(kind); return; }      // text/reflection: insert empty
    setAddKind(kind);
    if (newFileRef.current) { newFileRef.current.value = ''; newFileRef.current.click(); }
  };

  const onNewFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file || !addKind) return;
    setAddingMedia(true);
    try {
      const m = await api.adminUploadLearnMediaSmart(file);
      setBlocks((bs) => [...bs, { kind: addKind, text: '', mediaUri: m.uri, mediaContentType: m.contentType, mediaFilename: m.filename }]);
    } catch (err) { alert(err.message); }
    finally { setAddingMedia(false); setAddKind(null); }
  };

  // Persist metadata + blocks. Pass a statusOverride to also flip publish state.
  const persist = async (statusOverride) => {
    if (!meta?.title?.trim()) { alert('Title is required.'); return false; }
    const status = statusOverride || meta.status;
    setBusy(true);
    try {
      await api.adminUpdateLesson(id, {
        title: meta.title, subtitle: meta.subtitle, tier: meta.tier,
        status, durationSeconds: meta.durationSeconds, requiresPrevious: !!meta.requiresPrevious,
        headerColor: meta.headerColor || null,
        askAi: meta.askAi !== false,
        iconColor: meta.iconColor || '',
      });
      await api.adminSetBlocks(id, blocks.map((b) => ({
        kind: b.kind, text: b.text || null, mediaUri: b.mediaUri,
        mediaContentType: b.mediaContentType, mediaFilename: b.mediaFilename,
      })));
      if (statusOverride) setMeta((m) => ({ ...m, status }));
      return true;
    } catch (e) { alert(e.message); return false; }
    finally { setBusy(false); }
  };

  const save = async () => { if (await persist()) alert('Saved.'); };
  const togglePublish = () => persist(meta.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED');

  // Preview: save current edits silently, then show the lesson exactly as the
  // reader (Library) renders it — in a closable modal, no navigation.
  const [previewLesson, setPreviewLesson] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('web'); // 'web' | 'phone'
  const openPreview = async () => {
    if (!(await persist())) return;          // ensure the preview reflects current content
    setPreviewDevice('web');
    setPreviewing(true);
    try { setPreviewLesson(await api.getLearnLesson(id)); }
    catch (e) { alert(e.message); setPreviewing(false); }
  };

  // Ask-AI index (B4.4): build/refresh the lesson's RAG index on demand.
  const [aiIdx, setAiIdx] = useState(null); // { state, error }
  useEffect(() => {
    let stop = false;
    api.adminLessonIndexStatus(id).then((s) => { if (!stop) setAiIdx(s); }).catch(() => {});
    return () => { stop = true; };
  }, [id]);
  useEffect(() => {
    if (aiIdx?.state !== 'PROCESSING') return undefined;
    let stop = false;
    const t = setInterval(() => {
      api.adminLessonIndexStatus(id).then((s) => { if (!stop) setAiIdx(s); }).catch(() => {});
    }, 3000);
    return () => { stop = true; clearInterval(t); };
  }, [id, aiIdx?.state]);
  const buildIndex = async () => {
    try { await api.adminIndexLesson(id); setAiIdx({ state: 'PROCESSING' }); }
    catch (e) { alert(e.message); }
  };

  if (blocks == null || meta == null) return <div className="page"><Spinner /></div>;

  const backToLessons = () => navigate(meta.courseId ? `/admin/courses/${meta.courseId}` : '/admin/courses');
  const published = meta.status === 'PUBLISHED';

  return (
    <div className="page">
      <PageHead
        breadcrumb={<a onClick={backToLessons} style={{ cursor: 'pointer' }}>← Back to lessons</a>}
        title={meta.title || 'Lesson'}
        sub="Edit the lesson's details and content"
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="pill" style={{ background: published ? 'var(--green-bg)' : '#f3f4f6', color: published ? 'var(--green-text)' : 'var(--muted)' }}>{meta.status}</span>
            <button className="btn" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            <button className="btn" disabled={busy} onClick={openPreview}>Preview</button>
            <button className={`btn ${published ? '' : 'btn-primary'}`} disabled={busy} onClick={togglePublish}>
              {published ? 'Unpublish' : 'Save & Publish'}
            </button>
          </div>
        }
      />

      {previewing && (
        <Modal title="Preview — how learners see this lesson" width="min(1040px, 94vw)" onClose={() => { setPreviewing(false); setPreviewLesson(null); }}>
          {/* Web / Phone toggle. Phone renders the same content at phone width so
              the publisher sees how it reflows (the native app reuses this layout). */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 14 }}>
            {['web', 'phone'].map((d) => (
              <button
                key={d}
                className={`btn btn-sm ${previewDevice === d ? 'btn-primary' : ''}`}
                onClick={() => setPreviewDevice(d)}
              >
                {d === 'web' ? '🖥 Web' : '📱 Phone'}
              </button>
            ))}
          </div>
          {previewLesson
            ? (previewDevice === 'phone' ? (
                <div style={{
                  width: 390, maxWidth: '100%', margin: '0 auto',
                  border: '1px solid var(--border)', borderRadius: 24, overflow: 'hidden',
                  boxShadow: 'var(--shadow-card)', background: 'var(--bg, #f6f7fb)',
                }}>
                  <div className="page" style={{ padding: 12 }}><LessonPreviewBody lesson={previewLesson} /></div>
                </div>
              ) : (
                <div className="page" style={{ padding: 0 }}><LessonPreviewBody lesson={previewLesson} /></div>
              ))
            : <Spinner />}
        </Modal>
      )}

      {/* 2026-10-17 — same order as the other editors: colour + icon first. */}
      <Section title="Lesson details">
        <div className="form-field"><label>Header color</label>
          <ColorField value={meta.headerColor} onChange={(hex) => setMeta((m) => ({ ...m, headerColor: hex }))} /></div>
        <IconColorField value={meta.iconColor} coverValue={meta.headerColor} sameLabel="Same as the course's icon"
          onChange={(v) => setMeta((m) => ({ ...m, iconColor: v }))} />
        <LessonIconField lessonId={id} courseId={meta.courseId} title={meta.title} headerColor={meta.headerColor}
          iconColor={meta.iconColor}
          hasCover={!!meta.hasCover} onChange={(v) => setMeta((m) => ({ ...m, hasCover: v }))} />
        <div className="form-field"><label>Title <span className="req">*</span></label>
          <input className="input" value={meta.title} onChange={setM('title')} /></div>
        <div className="form-field"><label>Subtitle</label>
          <input className="input" value={meta.subtitle} onChange={setM('subtitle')} /></div>
        <div className="form-field" style={{ maxWidth: 240 }}><label>Access</label>
          <select className="input" value={meta.tier} onChange={setM('tier')}>{TIERS.map((t) => <option key={t}>{t}</option>)}</select></div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!meta.requiresPrevious}
            onChange={(e) => setMeta((m) => ({ ...m, requiresPrevious: e.target.checked }))} />
          Require earlier lessons to be completed before this one unlocks
        </label>
      </Section>

      <Section title="Content">
        {blocks.length === 0 && <div style={{ color: 'var(--muted)' }}>No blocks yet — add one from the toolbar below.</div>}
        {blocks.map((b, i) => (
          <BlockEditor key={i} block={b} index={i} total={blocks.length}
            onChange={(patch) => update(i, patch)} onRemove={() => remove(i)} onMove={(d) => move(i, d)} />
        ))}
      </Section>

      <Section title="Add a block">
        <input ref={newFileRef} type="file" style={{ display: 'none' }}
          accept={addKind ? ACCEPT[addKind] : undefined} onChange={onNewFile} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {ADD.map((a) => (
            <button key={a.kind} className="btn btn-indigo" title={a.title || undefined} style={{ fontSize: '0.9375rem', padding: '10px 18px', fontWeight: 600 }}
              disabled={addingMedia} onClick={() => startAdd(a.kind)}>+ {a.label}</button>
          ))}
          {addingMedia && <span style={{ alignSelf: 'center', fontSize: '0.8125rem', color: 'var(--muted)' }}>Uploading…</span>}
        </div>
      </Section>

      <Section title="Ask AI">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', cursor: 'pointer', marginBottom: 10 }}>
          <input type="checkbox" checked={meta.askAi !== false}
            onChange={(e) => setMeta((m) => ({ ...m, askAi: e.target.checked }))} />
          Show “Ask AI about this lesson” to learners
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn" disabled={aiIdx?.state === 'PROCESSING'} onClick={buildIndex}>
            {aiIdx?.state === 'PROCESSING' ? 'Indexing…' : '✶ Build / refresh Ask-AI index'}
          </button>
          {aiIdx?.state === 'FAILED' && <span style={{ fontSize: '0.75rem', color: '#b91c1c' }}>⚠ {aiIdx.error || 'failed'}</span>}
          {aiIdx?.state === 'PROCESSING' && <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>building the index…</span>}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 6 }}>
          Learners can “Ask AI about this lesson” using its text, transcripts, and attached docs.
          The index refreshes automatically when you Save; use this button to rebuild on demand
          (e.g. after generating a transcript).
        </div>
      </Section>
    </div>
  );
}

function BlockEditor({ block, index, total, onChange, onRemove, onMove }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [ingest, setIngest] = useState(null); // { status, error }
  const [tx, setTx] = useState(null); // transcript: { state, transcript, error }
  const isAV = block.kind === 'AUDIO' || block.kind === 'VIDEO';

  // Load any existing transcript state for this media block.
  useEffect(() => {
    if (!isAV || !block.mediaUri) { setTx(null); return undefined; }
    let stop = false;
    api.adminTranscriptStatus(block.mediaUri).then((s) => { if (!stop) setTx(s); }).catch(() => {});
    return () => { stop = true; };
  }, [block.mediaUri, isAV]);

  // While a transcription is running, poll until it finishes.
  useEffect(() => {
    if (!isAV || !block.mediaUri || tx?.state !== 'PROCESSING') return undefined;
    let stop = false;
    const t = setInterval(() => {
      api.adminTranscriptStatus(block.mediaUri).then((s) => { if (!stop) setTx(s); }).catch(() => {});
    }, 3000);
    return () => { stop = true; clearInterval(t); };
  }, [isAV, block.mediaUri, tx?.state]);

  const runTranscribe = async () => {
    if (!block.mediaUri) { alert('Upload a file first.'); return; }
    try {
      await api.adminTranscribe(block.mediaUri);
      setTx({ state: 'PROCESSING' });
    } catch (e) { alert(e.message); }
  };

  // Poll ingest status while the media is still processing (B4.0+).
  useEffect(() => {
    if (!block.mediaUri) { setIngest(null); return undefined; }
    let stop = false;
    const tick = () => api.adminIngestStatus(block.mediaUri)
      .then((s) => { if (!stop) setIngest(s); })
      .catch(() => {});
    tick();
    const t = setInterval(() => {
      if (ingest && (ingest.status === 'READY' || ingest.status === 'FAILED' || ingest.status === 'NONE')) return;
      tick();
    }, 2500);
    return () => { stop = true; clearInterval(t); };
  }, [block.mediaUri, ingest?.status]);

  const onPick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    // Small files go multipart through gw; large files (e.g. long video) take
    // the signed-URL direct-to-GCS path so they're not capped by Cloud Run.
    const large = file.size > MAX_UPLOAD_BYTES;
    setUploading(true);
    try {
      const m = large ? await api.adminUploadLearnMediaLarge(file) : await api.adminUploadLearnMedia(file);
      onChange({ mediaUri: m.uri, mediaContentType: m.contentType, mediaFilename: m.filename });
    } catch (err) { alert(err.message); }
    finally { setUploading(false); }
  };

  const ingestLabel = ingest && ingest.status !== 'NONE' ? ({
    QUEUED: '⏳ queued', PROCESSING: '⚙ processing…', READY: '✓ ready', FAILED: '⚠ failed',
  }[ingest.status] || ingest.status) : null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 8, background: 'var(--card-soft)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: 1, color: 'var(--muted)', fontWeight: 700 }}>{block.kind === 'TYPEFORM' ? 'EMBEDDED FORM' : block.kind}</span>
        <span style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" disabled={index === 0} onClick={() => onMove(-1)}>↑</button>
          <button className="btn btn-sm" disabled={index === total - 1} onClick={() => onMove(1)}>↓</button>
          <button className="btn btn-sm btn-danger" onClick={onRemove}>✕</button>
        </span>
      </div>
      {isMedia(block.kind) ? (
        <div>
          <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={onPick}
            accept={block.kind === 'AUDIO' ? 'audio/*' : block.kind === 'VIDEO' ? 'video/*' : '.pdf,.docx,.txt,.md,.csv'} />
          <div style={{ fontSize: '0.8125rem', color: 'var(--ink-3)', marginBottom: 8 }}>
            {block.mediaFilename ? `📎 ${block.mediaFilename}` : 'No file attached.'}
            {ingestLabel && <span style={{ marginLeft: 8, color: 'var(--muted)' }}>· {ingestLabel}</span>}
          </div>
          <button className="btn btn-sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? 'Uploading…' : (block.mediaFilename ? 'Replace file' : 'Upload file')}
          </button>

          {isAV && block.mediaUri && (
            <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-sm" disabled={tx?.state === 'PROCESSING'} onClick={runTranscribe}>
                  {tx?.state === 'PROCESSING' ? 'Transcribing…'
                    : tx?.state === 'READY' ? '↻ Regenerate transcript' : '✶ Generate transcript'}
                </button>
                {tx?.state === 'READY' && <span style={{ fontSize: '0.75rem', color: 'var(--green-text)' }}>✓ transcript ready</span>}
                {tx?.state === 'FAILED' && <span style={{ fontSize: '0.75rem', color: '#b91c1c' }}>⚠ {tx.error || 'failed'}</span>}
                {tx?.state === 'PROCESSING' && <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>can take a few minutes for long media</span>}
              </div>
              {tx?.state === 'READY' && tx.transcript && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--ink-3)' }}>Preview transcript</summary>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.8125rem', marginTop: 6, maxHeight: 240, overflow: 'auto' }}>{tx.transcript}</div>
                </details>
              )}
            </div>
          )}
        </div>
      ) : isEmbed(block.kind) ? (
        <EmbedField block={block} onChange={onChange} />
      ) : (
        <RichTextEditor value={block.text} onChange={(html) => onChange({ text: html })}
          placeholder={block.kind === 'REFLECTION' ? 'Reflection prompt…' : 'Body text…'} />
      )}
    </div>
  );
}

/**
 * Simple URL input for TYPEFORM / YOUTUBE embeds. The full URL (or bare ID)
 * is stored in the block's {@code text} field; the reader parses it into an
 * embed iframe on render.
 */
function EmbedField({ block, onChange }) {
  const isYoutube = block.kind === 'YOUTUBE';
  const placeholder = isYoutube
    ? 'https://www.youtube.com/watch?v=…  (or a bare video ID)'
    : 'https://tally.so/r/… or https://…typeform.com/to/… (any form URL)';
  return (
    <div>
      <input
        className="input"
        value={block.text || ''}
        onChange={(e) => onChange({ text: e.target.value })}
        placeholder={placeholder}
      />
      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 6 }}>
        {isYoutube
          ? 'Paste a YouTube link. Both youtube.com and youtu.be links work.'
          : 'Paste any form URL — Typeform, Tally, Google Forms, Jotform. The reader iframes it inline.'}
      </div>
    </div>
  );
}

// 2026-10-17 — optional icon image for the lesson header (else the course's
// icon is used). Uploaded immediately, like the course cover.
function LessonIconField({ lessonId, courseId, title, headerColor, iconColor, hasCover, onChange }) {
  const ref = useRef(null);
  // The course's icon — what learners see when the lesson has no image.
  const [course, setCourse] = useState(null);
  useEffect(() => {
    if (!courseId) return;
    api.adminListCourses()
      .then((cs) => setCourse((Array.isArray(cs) ? cs : []).find((c) => c.id === courseId) || null))
      .catch(() => {});
  }, [courseId]);
  const fallbackColor = course?.iconColor || course?.coverColor || headerColor;
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (e.target) e.target.value = '';
    if (!file) return;
    setBusy(true);
    try { await api.uploadLessonCover(lessonId, file); setVer((v) => v + 1); onChange(true); }
    catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.deleteLessonCover(lessonId); onChange(false); }
    catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="form-field">
      <label>Icon image (optional — shown instead of the icon color)</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {(() => {
          // Same order as the lesson header: own image → own icon color → the course's icon.
          const own = hasCover || !!iconColor;
          return (
            <>
              <Avatar name={own ? title : (course?.title || title)} colorSeed={courseId || lessonId} square size={48}
                src={hasCover ? `${api.lessonCoverUrl(lessonId)}?v=${ver}`
                  : (!iconColor && course?.coverImageUri ? api.courseCoverUrl(courseId) : null)}
                bg={iconColor ? cssBackground(iconColor) : (fallbackColor ? cssBackground(fallbackColor) : undefined)} />
              {!own && <span style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>Using the course's icon</span>}
            </>
          );
        })()}
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={onFile} />
        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => ref.current?.click()}>
          {busy ? 'Uploading…' : (hasCover ? 'Replace image' : 'Upload image')}
        </button>
        {hasCover && <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={remove}>Remove</button>}
      </div>
    </div>
  );
}
